/**
 * pagination.ts
 * Typed pagination contract — KC-5.1 / KC-5.2 / KC-5.3 / KC-5.4
 *
 * Lives in contracts-kernel so both backend (kernel) and frontend share
 * the same pagination shapes. ZERO Hono / Drizzle / infrastructure imports.
 *
 * Usage:
 *   // In a Hono route handler:
 *   const { page, pageSize } = parsePaginationQuery(c.req.query());
 *   // If query is invalid, parsePaginationQuery throws ZodError → global handler → 422
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Query input schema — raw query-string values arrive as strings
// ---------------------------------------------------------------------------

/**
 * Parses raw query-string pagination params.
 * Coerces strings to numbers, validates bounds.
 * KC-5.3: modules never parse raw query strings themselves.
 * KC-5.4: pageSize bounded 1–100.
 */
export const PaginationQuerySchema = z.object({
  /** 1-based page number. Default: 1. */
  page: z
    .string()
    .optional()
    .default('1')
    .pipe(z.coerce.number().int().min(1, 'page must be ≥ 1')),
  /** Rows per page. Default: 20. Max: 100. */
  pageSize: z
    .string()
    .optional()
    .default('20')
    .pipe(
      z.coerce
        .number()
        .int()
        .min(1, 'pageSize must be ≥ 1')
        .max(100, 'pageSize must be ≤ 100'),
    ),
});

export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;

/**
 * Parses raw query-string search/sort params, shared by every paginated list
 * route. `sortBy` stays a bare string here — each repo narrows it against its
 * own closed column whitelist (e.g. `PERSON_SORT_COLUMNS`) and falls back to
 * a safe default when the value isn't in that whitelist. Never interpolate
 * `sortBy` directly into SQL.
 */
export const SearchSortQuerySchema = z.object({
  search: z.string().trim().min(1, 'search must not be blank').optional(),
  sortBy: z.string().optional(),
  sortDir: z.enum(['asc', 'desc']).optional().default('asc'),
});

export type SearchSortQuery = z.infer<typeof SearchSortQuerySchema>;

/**
 * Parse and validate raw search/sort query params (typically from c.req.query()).
 * Throws ZodError on validation failure → the global error handler converts it to 422.
 */
export function parseSearchSortQuery(
  raw: Record<string, string | undefined>,
): SearchSortQuery {
  return SearchSortQuerySchema.parse(raw);
}

// ---------------------------------------------------------------------------
// Response shape — generic over the item type
// ---------------------------------------------------------------------------

/**
 * Typed paginated response.
 * KC-5.1: items is typed T[], not unknown[].
 */
export interface PaginatedResponse<T> {
  readonly items: T[];
  readonly totalCount: number;
  readonly totalPages: number;
  readonly page: number;
  readonly pageSize: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parse and validate raw pagination query params (typically from c.req.query()).
 * Throws ZodError on validation failure → the global error handler converts it to 422.
 * KC-5.3: modules call this helper, never parse query strings manually.
 */
export function parsePaginationQuery(
  raw: Record<string, string | undefined>,
): PaginationQuery {
  return PaginationQuerySchema.parse(raw);
}

/**
 * Build a typed PaginatedResponse from already-fetched items + total count.
 * totalPages is computed from totalCount / pageSize (ceiling).
 */
export function buildPaginatedResponse<T>(input: {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
}): PaginatedResponse<T> {
  const { items, totalCount, page, pageSize } = input;
  return {
    items,
    totalCount,
    page,
    pageSize,
    totalPages: Math.ceil(totalCount / pageSize),
  };
}
